import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { ModalShell } from '../../../components/ui/ModalFrame';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import userService from '../../../services/userService';
import authService from '../../../services/cognitoService';

const COUNTDOWN_SECONDS = 10;
const CONFIRMATION_TEXT = 'DELETE';
const DELETE_ACCOUNT_ERROR_MESSAGE =
  'Ocorreu um erro ao excluir a conta. Por favor, entre em contato com o suporte.';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const DeleteAccount = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!isDialogOpen) return;

    setCountdown(COUNTDOWN_SECONDS);
    setTypedConfirmation('');
    setErrorMessage(null);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isDialogOpen]);

  const isConfirmationValid =
    typedConfirmation.trim().toUpperCase() === CONFIRMATION_TEXT;

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      setErrorMessage(null);

      const result = await userService.deleteAccount();

      if (result?.success) {
        try {
          await fetch(`${API_BASE_URL}/logout`, {
            method: 'POST',
            credentials: 'include',
          });
        } finally {
          authService.logout();
        }
      } else {
        setErrorMessage(DELETE_ACCOUNT_ERROR_MESSAGE);
      }
    } catch {
      setErrorMessage(DELETE_ACCOUNT_ERROR_MESSAGE);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="rounded-lg border border-error/20 bg-background/50 p-4 md:p-5 transition-smooth">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
          <Icon name="Trash2" size={20} color="var(--color-error)" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Deletar Conta
          </h3>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Esta ação é permanente e não pode ser desfeita
          </p>
        </div>
      </div>

      <Button
        variant="destructive"
        iconName="Trash2"
        onClick={() => setIsDialogOpen(true)}
      >
        Deletar Conta
      </Button>

      {/* Confirmation dialog */}
      {isDialogOpen && (
        <ModalShell
          zClass={MODAL_LAYER.standard}
          backdropClassName="bg-black/50"
          panelClassName="bg-card rounded-lg max-w-lg w-full p-6 shadow-elevation-lg"
        >
          <h3 className="text-lg font-heading font-semibold text-foreground mb-3">
            Confirmar exclusão da conta
          </h3>

          <p className="text-sm text-muted-foreground mb-5">
            Ao encerrar sua conta, você estará excluindo todas as suas
            informações vinculadas à Neo Modelos. Isso inclui documentos
            gerados, licenças adquiridas e quaisquer outras informações
            persistidas no nosso sistema.
            <br />
            <br />
            Para confirmar, digite <strong>DELETE</strong> abaixo.
          </p>

          <Input
            type="text"
            value={typedConfirmation}
            onChange={(e) => setTypedConfirmation(e.target.value)}
            placeholder="Digite DELETE"
            disabled={isDeleting}
            error={errorMessage || undefined}
          />

          <div className="flex flex-col sm:flex-row gap-3 justify-end mt-6">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={
                countdown > 0 ||
                !isConfirmationValid ||
                isDeleting
              }
            >
              {countdown > 0
                ? `Deletar conta (${countdown}s)`
                : isDeleting
                ? 'Deletando...'
                : 'Deletar conta'}
            </Button>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default DeleteAccount;
